import { useRef, useState } from "react";
import { Polygon, Popup, useMapEvents } from "react-leaflet";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { BanderaRuc } from "./BanderaRuc";

const gs = (n) => (n ? Math.round(n).toLocaleString("es-PY") : "—");
const ha = (x) => x.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Ficha({ d, onIr, actual }) {
  if (d.cargando) return <p className="flex items-center gap-2 text-xs" data-testid="identificar-cargando"><Loader2 className="w-3.5 h-3.5 animate-spin" />Consultando el catastro (SNC)…</p>;
  if (d.error) return <p className="text-xs text-rose-700" data-testid="identificar-error">{d.error}</p>;
  if (!d.encontrada) return <p className="text-xs" data-testid="identificar-sin-parcela">{d.mensaje}</p>;
  const esta = actual.cod === d.cod && actual.padron === d.padron;
  return <div className="text-xs space-y-1 min-w-[220px]" data-testid="identificar-ficha">
    <p className="font-semibold text-sm" data-testid="identificar-titulo">{d.cod} · padrón {d.padron}{d.finca ? ` · finca ${d.finca}` : ""}{d.urbano ? " · urbano" : ""}</p>
    <p data-testid="identificar-titulares">{d.titulares?.length ? d.titulares.map((t) => `${t.nombre}${t.documento ? ` (Doc. ${t.documento})` : ""}`).join(" · ") : `Titular no disponible${d.fuente_titulares ? ` · ${d.fuente_titulares}` : ""}`}</p>
    {d.titulares?.map((t, i) => <BanderaRuc key={i} nombre={t.nombre} documento={t.documento} testid={`identificar-bandera-ruc-${i}`} />)}
    <p data-testid="identificar-superficie">{d.superficie ? `${ha(d.superficie.ha)} ha · ${d.superficie.m2.toLocaleString("es-PY")} m²` : "Superficie —"} · Valor fiscal Gs. {gs(d.valor_fiscal_gs)}</p>
    {d.alertas?.length > 0 && <p className="text-red-700 font-semibold" data-testid="identificar-alertas">{d.alertas.join(" · ")}</p>}
    <p className="text-[10px] text-slate-500">{d.nota}{d.fuente_titulares ? ` Titulares: ${d.fuente_titulares}.` : ""}</p>
    {esta ? <p className="text-[11px] text-[#1f7a4d]" data-testid="identificar-es-actual">Es la parcela que estás viendo</p>
      : !d.urbano && <button onClick={() => onIr({ cod: d.cod, padron: d.padron })} className="rounded-full bg-[#1f7a4d] text-white px-3 py-1 font-semibold" data-testid="identificar-ir">Ver ficha completa</button>}
  </div>;
}

/** Clic libre en el mapa: identifica la parcela catastral en ese punto (vecinas, enclavadas, cualquiera). */
export function IdentificarClic({ onIr, actual }) {
  const [sel, setSel] = useState(null);
  const n = useRef(0);
  useMapEvents({
    click: async (e) => {
      const id = ++n.current;
      const { lat, lng } = e.latlng;
      setSel({ latlng: e.latlng, d: { cargando: true } });
      try {
        const r = await api.get("/portal/titulares/identificar", { params: { lat, lng }, timeout: 70000 });
        if (id === n.current) setSel({ latlng: e.latlng, d: r.data });
      } catch (x) {
        const det = x?.response?.data?.detail;
        // 5xx del proxy (Cloudflare 520/522, HTML) o corte por tiempo: mensaje claro en vez del texto técnico
        const msg = typeof det === "string" && x?.response?.status < 520 ? det : "El catastro (SNC) está lento o no responde · reintentá en un minuto";
        if (id === n.current) setSel({ latlng: e.latlng, d: { error: msg } });
      }
    },
  });
  if (!sel) return null;
  return <>
    {sel.d.poligono?.map((a, i) => <Polygon key={`id-${i}`} positions={a} interactive={false} pathOptions={{ color: "#ffd60a", weight: 3, fillColor: "#ffd60a", fillOpacity: 0.15 }} />)}
    <Popup position={sel.latlng} eventHandlers={{ remove: () => setSel(null) }} autoPan><Ficha d={sel.d} onIr={onIr} actual={actual} /></Popup>
  </>;
}
